import React, { useState } from "react";
import { Modal, Button, Col, Row } from "react-bootstrap";
import Image from "./image/connect-online.svg";
import { useNavigate, useParams } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import AccessService from "@me-team/host/services/AccessService";
import { useTranslation } from "react-i18next";
import { currentCompanyIdVar, currentUserEmailVar } from "@me-team/host/main/globalVar/state";
import { useGetEmployeesTariffBlockingsQuery } from "@me-team/host/main/employeeList/graphql/employeeList.hooks";
import { Spinner } from "@services/ui-components";
import CommonQuestionsModal from "./CommonQuestionsModal";
import OnlineBookingSkipTutorialConfirmation from "./OnlineBookingSkipTutorialConfirmation";

interface ConnectModalProps {
    show: boolean;
    setOpenConnectModal: React.Dispatch<React.SetStateAction<boolean>>;
    toggleEmployeeLimitModal: () => void;
    onClose?: () => void;
}

const ConnectModal: React.FC<ConnectModalProps> = ({
    show,
    setOpenConnectModal,
    toggleEmployeeLimitModal,
    onClose,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentUserEmail = useReactiveVar(currentUserEmailVar);

    const [isCommonQuestionsOpen, setIsCommonQuestionsOpen] = useState(false);
    const [isSkipConfirmationOpen, setIsSkipConfirmationOpen] = useState(false);

    const handleClose = () => {
        setIsCommonQuestionsOpen(false);
        setIsSkipConfirmationOpen(false);
        onClose && onClose();
        setOpenConnectModal(false);
    };

    const openCommonQuestions = () => {
        setIsCommonQuestionsOpen(true);
    };

    const backToConnect = () => {
        setIsCommonQuestionsOpen(false);
    };

    const { data: employeesOnlyActive, loading: loadingTariffBlocking } = useGetEmployeesTariffBlockingsQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId || !show,
        context: {
            errorType: "local",
        },
        onError: (error) => {
            console.error(error);
        },
    });

    const start = (step: string) => {
        const isEmployeeLimitExceeded = !AccessService.getInstance().canAddEmployee(
            employeesOnlyActive?.user?.company?.employees?.employees,
            false
        );
        if (isEmployeeLimitExceeded) {
            toggleEmployeeLimitModal();
            handleClose();
        } else {
            localStorage.setItem("step", step);
            localStorage.setItem("run", "true");
            navigate(`/employees-list/${branchId}`, { state: { navigationStep: step } });
        }
    };
    const goToStep3 = () => {
        const accessService = AccessService.getInstance().isPremium;
        localStorage.setItem("step", accessService ? "4" : "3");
        localStorage.setItem("run", "true");
        navigate(`/employees-list/${branchId}`, {
            state: { mail: currentUserEmail, navigationStep: accessService ? "4" : "3" },
        });
    };

    if (loadingTariffBlocking) return <Spinner />;

    return (
        <>
            <Modal show={show && !isCommonQuestionsOpen} onHide={handleClose} centered>
                {isSkipConfirmationOpen ? (
                    <Modal.Body>
                        <OnlineBookingSkipTutorialConfirmation
                            onContinueTutorial={() => setIsSkipConfirmationOpen(false)}
                            onAfterSkipTutorial={handleClose}
                        />
                    </Modal.Body>
                ) : (
                    <>
                        <Modal.Header className="border-0" closeButton></Modal.Header>
                        <Modal.Body className="text-center pt-0 pb-0">
                            <h5 className="fw-normal mb-3">{t("Online booking connection")}</h5>
                            <Image />
                            <p className="mb-0  mt-3">{t("Basic steps to connect Online booking.")}</p>
                            <p className="mb-0">{t("Select the number of employees:")}</p>
                        </Modal.Body>
                        <Modal.Footer className="border-0 px-4 pt-4">
                            <Col>
                                <Button variant="primary" className="  w-100  " onClick={() => goToStep3()}>
                                    {t("I work alone")}
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    variant="success"
                                    className="btn btn-primary w-100 text-truncate "
                                    onClick={() => start("1")}
                                >
                                    {t("I have employee(s)")}
                                </Button>
                            </Col>
                            <Col xs={12} className="text-center w-100 m-0 py-6">
                                <Row className="align-items-center gx-2">
                                    <Col className="text-end">
                                        <Button
                                            variant="link"
                                            className="text-primary p-0 text-nowrap"
                                            onClick={openCommonQuestions}
                                        >
                                            {t("Frequently asked questions")}
                                        </Button>
                                    </Col>
                                    <Col xs="auto" className="d-flex justify-content-center">
                                        <span
                                            className="rounded-circle d-inline-block bg-grey"
                                            style={{ width: 3, height: 3 }}
                                        />
                                    </Col>
                                    <Col className="text-start">
                                        <Button
                                            variant="link"
                                            className="text-grey p-0 text-nowrap"
                                            onClick={() => setIsSkipConfirmationOpen(true)}
                                        >
                                            {t("Skip training")}
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Modal.Footer>
                    </>
                )}
            </Modal>

            <CommonQuestionsModal
                show={show && isCommonQuestionsOpen}
                variant="start"
                onBack={backToConnect}
                onComplete={backToConnect}
            />
        </>
    );
};

export default ConnectModal;
